import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import cx from 'classnames';

import { AnimationComponent } from '../../Animations';
import { BackgroundView } from '../background';

import { PageContext } from '../page';
import { useWidgetSelector, useContainerSelector } from '../core/hooks';
import { HotspotContext } from '../../../components/hotspot';
import { getBoxShadow } from '../../../services/cssHelper.service';

import { PAGE, SECTION_COLUMN } from '../../../constants/widgetTypes';

import styles from './hotspot.module.scss';


const HotspotWidgetComponent = ({ className, style, widgetProps, breakpoint, children }) => {

  const ref = React.useRef();

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const { zoom } = React.useContext(PageContext);

  const parent = useWidgetSelector(widgetProps.parent);
  const container = useContainerSelector(parent.uid);

  const hotspotContext = React.useContext(HotspotContext);
  const show = hotspotContext.active === id;

  const properties = _.get(widgetProps, 'properties', {});
  const animation = _.get(widgetProps, `properties.desktop.animation`) || {};
  const background = _.get(widgetProps, 'properties.desktop.background');
  const trigger = _.get(widgetProps, `properties.desktop.trigger`);

  var widgetLayoutStyle = {
    ...style,
    ...getLayoutStyles(parent, breakpoint, container, zoom),
  }

  var widgetStyles = {
    ...getStyles(widgetProps, breakpoint),
    width: '100%',
    height: style.height,
    borderStyle: 'solid',
    position: 'relative'
  }

  var backgroundStyle = getBackgroundStyle(properties, breakpoint);

  useEffect(() => {
    if (trigger === 'hover') {
      ref.current.addEventListener('mouseover', handleMouseOver);
      ref.current.addEventListener('mouseout', handleMouseOut);

      return () => {
        if (ref.current) {
          ref.current.removeEventListener('mouseover', handleMouseOver);
          ref.current.removeEventListener('mouseout', handleMouseOut);
        }
      }
    }
  }, []);

  const handleMouseOver = (e) => {
    hotspotContext.onMouseOver(e, parent, widgetProps);
  }

  const handleMouseOut = (e) => {
    hotspotContext.onMouseOut(e, parent, widgetProps);
  }

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <div ref={ref} className={cx(styles.overlayContainer, "overlay_container", { [styles.show]: show })}>
      <div id={id}
        className={cx(className, styles.hotspotWidget, 'cs_hotspot_widget', {
          [styles.mobile]: breakpoint === 'mobile'
        })}
        data-widget-id={id}
        data-widget-type={type}
        data-container={true}
        data-selectable={true}
        style={widgetLayoutStyle}
      >
        <AnimationComponent triggerOnce={true} {...animation}>
          <div style={widgetStyles}>
            <BackgroundView background={background} style={backgroundStyle} breakpoint={breakpoint} />
            {children}
          </div>
        </AnimationComponent>
      </div>
    </div>
  )
}

const hotspotWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const HotspotWidget = React.memo(HotspotWidgetComponent, hotspotWidgetEquals);


/**
 * Generate Styles
 *
*/
function getStyles(widget, breakpoint) {
  if (!widget) {
    return null;
  }

  const border = _.get(widget, `properties.${breakpoint}.border`);

  var styles = {};
  const topLeft = _.get(border, 'radius.topLeft', null);
  const topRight = _.get(border, 'radius.topRight', null);
  const bottomLeft = _.get(border, 'radius.bottomLeft', null);
  const bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(border, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(border, 'width', 0);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  //DropShadow
  const shadow = _.get(widget, `properties.${breakpoint}.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  styles = {
    ...styles,
    ...shadowStyle
  }

  return styles;
}

function getBackgroundStyle(properties, breakpoint) {
  var styles = {
    overflow: 'hidden'
  };

  const defaultProperties = _.get(properties, `${breakpoint}`);

  const border = _.get(defaultProperties, 'border');

  const borderWidthString = _.get(border, 'width') || 0;
  var borderWidget = parseInt(borderWidthString) + 'px';

  var topLeft = _.get(border, 'radius.topLeft', null);
  var topRight = _.get(border, 'radius.topRight', null);
  var bottomLeft = _.get(border, 'radius.bottomLeft', null);
  var bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = `calc(${parseInt(topLeft) + 'px'} - ${borderWidget})`;
  }

  if (topRight) {
    styles['borderTopRightRadius'] = `calc(${parseInt(topRight) + 'px'} - ${borderWidget})`;
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = `calc(${parseInt(bottomLeft) + 'px'} - ${borderWidget})`;
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = `calc(${parseInt(bottomRight) + 'px'} - ${borderWidget})`;
  }

  return styles;
}

function getLayoutStyles(parent, breakpoint, container, zoom) {
  var styles = {};
  if (typeof window === 'undefined') {
    return styles;
  }

  const parentPosition = getBoundingRectFromWidget(parent.uid, { x: 0, y: 0 });

  const parentElement = document.getElementById(parent.uid);

  if (!parentElement) {
    return
  }

  var parentComputedStyles = window.getComputedStyle(parentElement);

  var parentLeftMargin = parentComputedStyles.marginLeft;

  if (container) {
    const minWidth = _.get(container, `properties.${breakpoint}.container.minWidth`, 375);
    const alignment = _.get(container, `properties.${breakpoint}.container.alignment`) || 'center';

    var multiplier = 0.5;
    switch (alignment) {
      case 'left':
        multiplier = 0;
        break;

      case 'right':
        multiplier = 1;
        break;

      case 'center':
      default:
        multiplier = 0.5;
        break;
    }

    const containerElement = document.getElementById(container.uid);

    if (!containerElement) {
      return styles;
    }

    const containerRect = containerElement.getBoundingClientRect();

    var order = container.type !== PAGE ? container.order - 1 : 0;
    var marginPostion = (breakpoint === 'mobile' || container.type !== SECTION_COLUMN ? 0 : (containerRect.width * order) / zoom) + parentPosition.x;

    var marginLeft = `${parentPosition.x}px`;
    if (minWidth) {
      marginLeft = `calc(calc((100% - ${breakpoint === 'desktop' ? 1200 : minWidth}px) * ${multiplier}) + ${marginPostion}px`

      if (minWidth < 1200) {
        marginLeft = `calc(calc((100% - ${breakpoint === 'desktop' ? 1200 : minWidth}px) * ${multiplier}) + ${marginPostion}px - ${parentLeftMargin})`;
      }
    }

    var marginTop = `${parentPosition.y}px`;
    if (breakpoint === 'mobile' && container.type === SECTION_COLUMN) {
      marginTop = `${(containerRect.height * order) + parentPosition.y}px`
    }

    if (parentPosition) {
      styles['marginLeft'] = marginLeft;
      styles['marginTop'] = marginTop;
    }
  }

  return styles;
}

function getBoundingRectFromWidget(widget, widgetRect) {
  var widgetElement = document.getElementById(widget);

  if (!widgetElement) {
    return widgetRect;
  }

  const computedStyle = window.getComputedStyle(widgetElement);

  const transformValue = computedStyle.getPropertyValue('transform');
  if (transformValue) {
    const match = transformValue.match(/matrix\(([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+)\)/);

    if (match) {
      const horizontalTranslation = parseFloat(match[5]);
      const verticalTranslation = parseFloat(match[6]);

      widgetRect['x'] = widgetRect.x + horizontalTranslation;
      widgetRect['y'] = widgetRect.y + verticalTranslation;
    }
  }

  var parentElement = findClosestParent(widgetElement.parentElement);

  if (parentElement.classList.contains('cs_page_root')) {
    return widgetRect;
  }

  return getBoundingRectFromWidget(parentElement.id, widgetRect);
}

function findClosestParent(element) {
  while (element) {
    if (element.id && element.id.startsWith("widget_")) {
      return element; // Found a parent with the specified prefix
    }
    element = element.parentElement; // Move up the DOM tree
  }
  return null; // No matching parent found
}
